import React, { useState } from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import type {
  BillChipKind, DashboardBill, DashboardBills, DashboardCategorySpend, DashboardSpending,
} from '../../services/dashboard-service';
import { BillTransactionsDialog, type BillDrillTarget } from '../bills/BillTransactionsDialog';
import { EmptyLine, Figure, NavigateFn, SectionCard, monthLabel, num, shortDate, useMoney } from './shared';

/** Chip colours by meaning. Status colours carry a label, never colour alone. */
const CHIP_STYLE: Record<BillChipKind, { bg: string; fg: string }> = {
  paid: { bg: 'var(--success-soft)', fg: 'var(--success-text)' },
  no_balance: { bg: 'var(--glass-2)', fg: 'var(--text-secondary)' },
  nothing_due: { bg: 'var(--glass-2)', fg: 'var(--text-secondary)' },
  skipped: { bg: 'var(--glass-2)', fg: 'var(--text-secondary)' },
  scheduled: { bg: 'var(--info-soft)', fg: 'var(--info-text)' },
  due: { bg: 'var(--warn-soft)', fg: 'var(--warn-text)' },
  overdue: { bg: 'var(--danger-soft)', fg: 'var(--danger-text)' },
  needs_review: { bg: 'var(--danger-soft)', fg: 'var(--danger-text)' },
  not_seen: { bg: 'var(--danger-soft)', fg: 'var(--danger-text)' },
};

export const BillStatusChip: React.FC<{ chip: DashboardBill['chip'] }> = ({ chip }) => {
  const s = CHIP_STYLE[chip.kind] ?? CHIP_STYLE.due;
  return (
    <Chip size="small" label={chip.label}
      sx={{ background: s.bg, color: s.fg, fontWeight: 600, height: 22, flexShrink: 0 }} />
  );
};

/**
 * One bill. The whole row is a button: clicking it (or Enter/Space on it)
 * opens the transactions behind the bill, including a "$0 · nothing due" one.
 */
const BillRow: React.FC<{ bill: DashboardBill; showDue?: boolean; onOpen: (b: DashboardBill) => void }> = ({
  bill, showDue, onOpen,
}) => {
  const money = useMoney();
  const amount = bill.amount_due != null && num(bill.amount_due) > 0 ? money(bill.amount_due) : '—';
  return (
    <Box role="button" tabIndex={0}
      aria-label={`${bill.payee_name} ${amount === '—' ? 'no amount' : amount}, ${bill.chip.label}. View transactions`}
      onClick={() => onOpen(bill)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(bill);
        }
      }}
      sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, px: 0.5, mx: -0.5, cursor: 'pointer',
        borderTop: '1px solid var(--border-soft)', borderRadius: 1,
        '&:hover': { background: 'var(--glass-1)' },
        '&:hover .bill-amount': { textDecoration: 'underline' },
        '&:focus-visible': { outline: '2px solid var(--accent)', outlineOffset: -2 } }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {bill.payee_name}
        </Typography>
        {/* The chip already says "Due <date>" for a plain due bill. */}
        {showDue && bill.due_date && bill.chip.kind !== 'due' && (
          <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>Due {shortDate(bill.due_date)}</Typography>
        )}
      </Box>
      <Typography variant="body2" className="fw-num bill-amount" sx={{ fontWeight: 600, flexShrink: 0 }}>{amount}</Typography>
      <BillStatusChip chip={bill.chip} />
    </Box>
  );
};

/** Section 3: this period's bills, what is paid, and what comes next. */
export const BillsThisMonth: React.FC<{ bills: DashboardBills; onNavigate: NavigateFn }> = ({ bills, onNavigate }) => {
  const money = useMoney();
  const outstanding = num(bills.outstanding);
  const [drill, setDrill] = useState<BillDrillTarget | null>(null);
  const open = (b: DashboardBill) => setDrill({ id: b.id, payeeName: b.payee_name });
  return (
    <SectionCard title="Bills this month" subtitle={monthLabel(bills.period)} onNavigate={onNavigate}
      action={{ label: 'All bills', link: { to: '/bills' } }}>
      {bills.items.length === 0 && bills.upcoming.length === 0 ? (
        <EmptyLine>No bills for this month yet. Add payees on the Bills page to track the cycle.</EmptyLine>
      ) : (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1.5, mb: 1.5 }}>
            <Figure label="Billed" value={money(bills.total_billed)} />
            <Figure label="Paid" value={money(bills.total_paid)} tone="in" />
            <Figure label="Outstanding" value={money(bills.outstanding)} tone={outstanding > 0 ? 'warn' : 'plain'} />
          </Box>
          <Box aria-label="This month's bills">
            {bills.items.map((b) => <BillRow key={b.id} bill={b} onOpen={open} />)}
          </Box>
          {bills.upcoming.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="overline" sx={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                Next up
              </Typography>
              <Box aria-label="Upcoming bills">
                {bills.upcoming.map((b) => <BillRow key={`up-${b.id}`} bill={b} showDue onOpen={open} />)}
              </Box>
            </Box>
          )}
        </>
      )}
      <BillTransactionsDialog target={drill} onClose={() => setDrill(null)} />
    </SectionCard>
  );
};

/** End of a "YYYY-MM" month as YYYY-MM-DD. */
function monthEnd(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${ym}-${String(last).padStart(2, '0')}`;
}

/** Where a click on a category bar goes: that category, that month. */
export function categoryLink(c: DashboardCategorySpend, month: string) {
  const search: Record<string, string> = { start_date: `${month}-01`, end_date: monthEnd(month) };
  if (c.category_id) search.category_id = c.category_id;
  else search.category = c.name;
  return { to: '/transactions', search };
}

const Delta: React.FC<{ c: DashboardCategorySpend }> = ({ c }) => {
  const d = num(c.delta);
  if (num(c.previous) === 0) {
    return <Typography variant="caption" sx={{ color: 'var(--text-faint)' }}>new</Typography>;
  }
  // Spending MORE is the bad direction, so up reads as outflow.
  const color = d > 0 ? 'var(--outflow-text)' : d < 0 ? 'var(--inflow-text)' : 'var(--text-faint)';
  return (
    <Typography variant="caption" className="fw-num" sx={{ color, fontWeight: 600 }}>
      {d > 0 ? '▲' : d < 0 ? '▼' : ''}{c.delta_percent != null ? ` ${Math.abs(num(c.delta_percent))}%` : ''}
    </Typography>
  );
};

/**
 * Section 4: top categories this month as horizontal bars, with last month
 * and the three-month average as tick marks on the same scale. One measure
 * (spend), one axis; the ticks are references, not a second series.
 */
export const SpendingByCategory: React.FC<{ spending: DashboardSpending; onNavigate: NavigateFn }> = ({
  spending, onNavigate,
}) => {
  const money = useMoney();
  const cats = spending.categories;
  const max = Math.max(1, ...cats.flatMap((c) => [num(c.current), num(c.previous), num(c.avg_3m)]));
  const pct = (v: string) => `${Math.min(100, (num(v) / max) * 100)}%`;
  return (
    <SectionCard title="Spending by category"
      subtitle={`${monthLabel(spending.month)} · day ${spending.days_elapsed} of ${spending.days_in_month} · ${money(spending.total_current)} so far vs ${money(spending.total_previous)} last month`}
      onNavigate={onNavigate} action={{ label: 'Reports', link: { to: '/reports' } }}>
      {cats.length === 0 ? (
        <EmptyLine>No spending recorded this month or last.</EmptyLine>
      ) : (
        <>
          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }} aria-label="Top spending categories">
            {cats.map((c) => (
              <Tooltip key={c.category_id ?? c.name} placement="top-start" arrow
                title={`${c.name}: ${money(c.current)} this month · ${money(c.previous)} last month · ${money(c.avg_3m)} 3-month average`}>
                <Box component="li" role="button" tabIndex={0}
                  aria-label={`${c.name} ${money(c.current)}, view transactions`}
                  onClick={() => onNavigate(categoryLink(c, spending.month))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onNavigate(categoryLink(c, spending.month));
                    }
                  }}
                  sx={{ py: 0.6, cursor: 'pointer', borderRadius: 1, px: 0.5, mx: -0.5,
                    '&:hover': { background: 'var(--glass-1)' },
                    '&:focus-visible': { outline: '2px solid var(--accent)' } }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography variant="body2" sx={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {c.name}
                    </Typography>
                    <Delta c={c} />
                    <Typography variant="body2" className="fw-num" sx={{ fontWeight: 600, minWidth: 84, textAlign: 'right' }}>
                      {money(c.current)}
                    </Typography>
                  </Box>
                  <Box sx={{ position: 'relative', height: 8, mt: 0.5, borderRadius: 4, background: 'var(--glass-2)' }}>
                    <Box sx={{ position: 'absolute', inset: 0, width: pct(c.current), background: 'var(--accent)',
                      borderRadius: 4 }} />
                    {num(c.previous) > 0 && (
                      <Box data-testid="prev-tick" sx={{ position: 'absolute', top: -3, bottom: -3, width: 2,
                        left: pct(c.previous), background: 'var(--text)', borderRadius: 1 }} />
                    )}
                    {num(c.avg_3m) > 0 && (
                      <Box sx={{ position: 'absolute', top: -3, bottom: -3, width: 2, left: pct(c.avg_3m),
                        background: 'var(--text-faint)', borderRadius: 1 }} />
                    )}
                  </Box>
                </Box>
              </Tooltip>
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mt: 1.25, flexWrap: 'wrap' }} aria-hidden>
            {[
              { swatch: <Box sx={{ width: 12, height: 8, borderRadius: 2, background: 'var(--accent)' }} />, label: 'This month' },
              { swatch: <Box sx={{ width: 2, height: 12, background: 'var(--text)' }} />, label: 'Last month' },
              { swatch: <Box sx={{ width: 2, height: 12, background: 'var(--text-faint)' }} />, label: '3-month avg' },
            ].map((l) => (
              <Box key={l.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {l.swatch}
                <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>{l.label}</Typography>
              </Box>
            ))}
          </Box>
        </>
      )}
    </SectionCard>
  );
};
